import { parsePossiblyBrokenJson } from './common';
import { escapeRegExp, getProtectedRanges, isProtected } from './citations';

const PERSON_NAME_KEYS = [
  'name',
  'display_name',
  'full_name',
  'fullname',
  'player_name',
  'common_name',
  'person_name',
  'athlete_name',
  'athlete',
];

const IMAGE_KEYS = [
  'image_path',
  'image',
  'image_url',
  'photo',
  'photo_url',
  'avatar',
  'avatar_url',
  'headshot',
  'headshot_url',
  'thumbnail',
  'thumbnail_url',
  'profile_image',
  'profile_picture',
  'img',
  'picture',
  'pic',
];

const PERSON_SIGNALS = [
  'common_name',
  'firstname',
  'lastname',
  'first_name',
  'last_name',
  'height',
  'weight',
  'date_of_birth',
];

export const ENTITY_TYPES = {
  player: {
    label: 'Players',
    singular: 'Player',
    nestedKey: 'player',
    nameKeys: PERSON_NAME_KEYS,
    imageKeys: IMAGE_KEYS,
    signals: [...PERSON_SIGNALS, 'detailed_position_id', 'jersey_number'],
    tools: ['get_player', 'get_squad', 'get_topscorers', 'get_totw'],
  },
  team: {
    label: 'Teams',
    singular: 'Team',
    nestedKey: 'team',
    nameKeys: ['name', 'team_name', 'club_name'],
    imageKeys: IMAGE_KEYS,
    signals: ['short_code', 'founded', 'venue', 'national_team'],
    tools: ['get_team', 'get_rivals'],
  },
  league: {
    label: 'Leagues',
    singular: 'League',
    nestedKey: 'league',
    nameKeys: ['name', 'league_name'],
    imageKeys: IMAGE_KEYS,
    signals: ['currentseason', 'current_season', 'sub_type'],
    tools: ['get_league'],
  },
};

const MIXED_TOOLS = ['search', 'get_transfers'];

const TOOL_META = {
  get_player: { label: 'Player', kind: 'entity' },
  get_coach: { label: 'Coach', kind: 'entity' },
  get_referee: { label: 'Referee', kind: 'entity' },
  get_team: { label: 'Team', kind: 'entity' },
  get_league: { label: 'League', kind: 'entity' },
  get_venue: { label: 'Venue', kind: 'entity' },
  get_squad: { label: 'Squad', kind: 'list' },
  get_topscorers: { label: 'Top scorers', kind: 'list' },
  get_totw: { label: 'Team of the week', kind: 'list' },
  get_rivals: { label: 'Rivals', kind: 'list' },
  search: { label: 'Search', kind: 'list' },
  get_transfers: { label: 'Transfers', kind: 'list' },
  get_season_stats: { label: 'Season stats', kind: 'stats' },
  get_historic_seasons: { label: 'Seasons', kind: 'list' },
  get_fixture_details: { label: 'Fixture details', kind: 'keyvalue' },
  get_matches: { label: 'Matches', kind: 'list' },
  get_match_preview: { label: 'Match preview', kind: 'keyvalue' },
  get_standings: { label: 'Standings', kind: 'table' },
  get_odds: { label: 'Odds', kind: 'list' },
  get_commentaries: { label: 'Commentaries', kind: 'list' },
  get_pressure_index: { label: 'Pressure index', kind: 'keyvalue' },
  get_match_facts: { label: 'Match facts', kind: 'keyvalue' },
  get_fixture_news: { label: 'News', kind: 'prose' },
  get_fixture_lineup_stats: { label: 'Lineup stats', kind: 'list' },
};

const LABEL_VALUE_KEYS = ['name', 'title', 'label', 'developer_name', 'code'];

const MAX_DETAILS = 80;

function toObject(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') return parsePossiblyBrokenJson(value);
  return value;
}

function isObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value);
}

function isScalar(value) {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}

function pickString(obj, keys) {
  for (const key of keys) {
    const value = obj?.[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

function composeName(obj) {
  const first = pickString(obj, ['firstname', 'first_name']);
  const last = pickString(obj, ['lastname', 'last_name']);
  if (first && last) return `${first} ${last}`;
  return first || last || '';
}

function nameOf(obj, nameKeys) {
  if (!isObject(obj)) return '';
  return pickString(obj, nameKeys) || composeName(obj);
}

function hasAnySignal(obj, signals) {
  return signals.some(key => obj?.[key] != null && obj?.[key] !== '');
}

function looksLikeEntityOfType(item, type) {
  if (!isObject(item)) return false;
  const config = ENTITY_TYPES[type];
  const nestedKey = config.nestedKey || 'player';
  if (isObject(item[nestedKey])) {
    if (nameOf(item[nestedKey], config.nameKeys) || hasAnySignal(item[nestedKey], config.signals)) return true;
  }
  if (hasAnySignal(item, config.signals)) return true;
  return Boolean(nameOf(item, config.nameKeys));
}

function classifyEntity(item) {
  if (!isObject(item)) return null;
  const rawType = String(item?.type || item?.entity_type || '').toLowerCase();
  if (rawType === 'player' || rawType === 'players') return 'player';
  if (rawType === 'team' || rawType === 'teams' || rawType === 'club' || rawType === 'clubs') return 'team';
  if (rawType === 'league' || rawType === 'leagues' || rawType === 'competition') return 'league';
  if (hasAnySignal(item, ENTITY_TYPES.player.signals)) return 'player';
  if (hasAnySignal(item, ENTITY_TYPES.team.signals)) return 'team';
  if (hasAnySignal(item, ENTITY_TYPES.league.signals)) return 'league';
  return null;
}

function detectToolType(toolName) {
  const name = String(toolName || '').toLowerCase();
  if (MIXED_TOOLS.some(tool => name.includes(tool))) return 'mixed';
  for (const [type, config] of Object.entries(ENTITY_TYPES)) {
    if (config.tools.some(tool => name.includes(tool))) return type;
  }
  return null;
}

function humanize(key) {
  return String(key)
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function namedString(obj) {
  if (!isObject(obj)) return '';
  for (const key of LABEL_VALUE_KEYS) {
    const value = obj[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

function extractTeamNames(nested) {
  const names = [];
  const add = value => {
    if (typeof value === 'string' && value.trim()) {
      names.push(value.trim());
    } else if (isObject(value)) {
      const name = value?.team?.name || value?.name;
      if (typeof name === 'string' && name.trim()) names.push(name.trim());
    }
  };

  const teams = nested?.teams;
  if (Array.isArray(teams)) teams.forEach(add);
  else add(teams);

  return [...new Set(names)];
}

function flattenStats(statistics) {
  const out = [];
  if (!Array.isArray(statistics)) return out;

  for (const stat of statistics) {
    const details = Array.isArray(stat?.details) ? stat.details : [];
    for (const detail of details) {
      if (out.length >= 12) return out;
      const label = detail?.type?.name;
      if (!label) continue;
      let value = detail?.value;
      if (isObject(value)) value = value.total ?? value.value ?? JSON.stringify(value);
      if (value == null || value === '') continue;
      out.push({ key: `stat-${label}`, label: humanize(label), value });
    }
  }

  return out;
}

function flattenAny(label, value, out, depth = 0) {
  if (out.length >= MAX_DETAILS) return;
  if (value == null || value === '') return;

  if (isScalar(value)) {
    out.push({ key: label, label, value });
    return;
  }

  if (Array.isArray(value)) {
    if (!value.length) return;
    if (value.every(isScalar)) {
      out.push({ key: label, label, value: value.join(', ') });
      return;
    }
    value.forEach((item, index) => {
      if (out.length >= MAX_DETAILS) return;
      const itemLabel = `${label} ${index + 1}`;
      if (isScalar(item)) {
        out.push({ key: `${label}-${index}`, label: itemLabel, value: item });
      } else if (isObject(item)) {
        const named = namedString(item);
        if (named) {
          out.push({ key: `${label}-${index}`, label: itemLabel, value: named });
        } else if (depth < 4) {
          flattenObject(item, itemLabel, out, depth + 1);
        }
      }
    });
    return;
  }

  if (isObject(value)) {
    const named = namedString(value);
    if (named) {
      out.push({ key: label, label, value: named });
    } else if (depth < 4) {
      flattenObject(value, label, out, depth + 1);
    }
  }
}

function flattenObject(obj, prefix, out, depth = 0) {
  Object.entries(obj || {}).forEach(([key, value]) => {
    const label = prefix ? `${prefix} ${humanize(key)}` : humanize(key);
    flattenAny(label, value, out, depth);
  });
}

function normalizeEntity(raw, type, index) {
  const config = ENTITY_TYPES[type];
  const nestedKey = config.nestedKey || 'player';
  const nested =
    isObject(raw?.[nestedKey]) &&
    (nameOf(raw[nestedKey], config.nameKeys) || hasAnySignal(raw[nestedKey], config.signals))
      ? raw[nestedKey]
      : raw;
  const merged = nested !== raw ? { ...raw, ...nested } : nested;

  const name = nameOf(merged, config.nameKeys) || config.singular;
  const image = pickString(merged, config.imageKeys);

  const aliases = [];
  for (const alias of [
    pickString(merged, ['common_name']),
    pickString(merged, ['lastname', 'last_name']),
  ]) {
    if (alias && alias.toLowerCase() !== name.toLowerCase() && !aliases.some(a => a.toLowerCase() === alias.toLowerCase())) {
      aliases.push(alias);
    }
  }

  const details = [];

  if (type === 'player') {
    const teamNames = extractTeamNames(merged);
    if (teamNames.length) details.push({ key: 'team', label: 'Team', value: teamNames.join(', ') });
  }

  details.push(...flattenStats(merged?.statistics));

  const skipKeys = new Set([
    ...config.nameKeys,
    ...config.imageKeys,
    'firstname',
    'lastname',
    'first_name',
    'last_name',
    'player',
    'teams',
    'statistics',
    nestedKey,
  ]);

  Object.entries(merged || {}).forEach(([key, value]) => {
    const lower = key.toLowerCase();
    if (skipKeys.has(lower)) return;
    if (value == null || value === '') return;
    flattenAny(humanize(key), value, details);
  });

  return { id: `${type}:${index}`, type, name, image, aliases, details, raw };
}

function collectObjects(value, type, out) {
  if (Array.isArray(value)) {
    if (value.length && value.every(isObject)) {
      if (value.some(item => looksLikeEntityOfType(item, type))) {
        value.forEach(item => out.push(item));
      } else {
        value.forEach(item => collectObjects(item, type, out));
      }
    } else {
      value.forEach(item => collectObjects(item, type, out));
    }
    return;
  }

  if (isObject(value)) {
    if (looksLikeEntityOfType(value, type)) {
      out.push(value);
      return;
    }
    Object.values(value).forEach(v => collectObjects(v, type, out));
  }
}

function collectMixed(value, grouped) {
  if (Array.isArray(value)) {
    value.forEach(item => {
      if (isObject(item)) {
        const type = classifyEntity(item);
        if (type) grouped[type].push(item);
        else collectMixed(item, grouped);
      }
    });
    return;
  }
  if (isObject(value)) {
    const type = classifyEntity(value);
    if (type) grouped[type].push(value);
    else Object.values(value).forEach(v => collectMixed(v, grouped));
  }
}

function emptyGroups() {
  return { player: [], team: [], league: [] };
}

/* ── Key-aware team/league detection ───────────────────────────────────
   Club/season names often live in tool results even when get_team/get_league
   weren't called (e.g. get_player's currentTeam, get_matches' home/away/league,
   get_standings rows, transfers). These walkers find them by key + shape. */

const TEAM_CONTEXT_KEYS = new Set([
  'team', 'teams', 'club', 'clubs',
  'home', 'away', 'home_team', 'away_team',
  'fromteam', 'toteam', 'from_team', 'to_team',
  'participant', 'participants',
  'currentteam', 'current_team',
  'overview',
]);

const LEAGUE_CONTEXT_KEYS = new Set([
  'league', 'leagues', 'season', 'seasons',
  'competition', 'competitions',
  'currentseason', 'current_season',
  'tournament', 'tournaments', 'division', 'divisions',
]);

function entityNameInObject(obj, type) {
  const keys = type === 'team'
    ? ['name', 'team_name', 'club_name', 'title', 'label']
    : ['name', 'league_name', 'season_name', 'competition_name', 'tournament_name', 'title', 'label'];
  return pickString(obj, keys);
}

function isContextEntity(obj, type, keyContext) {
  if (!isObject(obj)) return false;
  const config = ENTITY_TYPES[type];
  const key = String(keyContext || '').toLowerCase();
  const contextSet = type === 'team' ? TEAM_CONTEXT_KEYS : LEAGUE_CONTEXT_KEYS;

  const name = entityNameInObject(obj, type);
  if (!name) return false;

  // Strong type signal (short_code/founded/venue | currentseason/sub_type).
  if (hasAnySignal(obj, config.signals)) return true;

  // Named object under a team/league context key. Id is optional here — some
  // payloads only carry the name; the id can be resolved later on click.
  if (contextSet.has(key)) return true;

  return false;
}

function collectContextEntities(value, type, out, keyContext) {
  if (Array.isArray(value)) {
    for (const item of value) collectContextEntities(item, type, out, keyContext);
    return;
  }
  if (!isObject(value)) return;

  if (isContextEntity(value, type, keyContext)) {
    out.push(value);
    return;
  }

  for (const [key, v] of Object.entries(value)) {
    collectContextEntities(v, type, out, key);
  }
}

export function extractEntities(steps) {
  const grouped = emptyGroups();
  if (!Array.isArray(steps)) return grouped;

  for (const step of steps) {
    if (step?.tool_result == null) continue;
    const parsed = toObject(step.tool_result);
    const detected = detectToolType(step?.tool_name);

    if (detected === 'mixed') {
      collectMixed(parsed, grouped);
    } else if (detected) {
      collectObjects(parsed, detected, grouped[detected]);
    }

    // Also scan every tool result for club/season entities so the team/league
    // names surface even when get_team/get_league weren't called.
    if (detected !== 'team') collectContextEntities(parsed, 'team', grouped.team, '');
    if (detected !== 'league') collectContextEntities(parsed, 'league', grouped.league, '');
  }

  Object.keys(grouped).forEach(type => {
    grouped[type] = grouped[type].map((item, index) => normalizeEntity(item, type, index));
  });

  return grouped;
}

/* Resolved players for the chat "Charts" feature — the player entities the
   "Players" button highlights, reduced to { id (Sportmonks id), name }.
   Only players whose name actually appears in the response text are kept,
   so the dropdown matches exactly what gets highlighted. */
function entityNameInText(entity, text) {
  const source = String(text || '');
  if (!source) return false;

  const name = String(entity?.name || '').replace(/\s+/g, ' ').trim();
  const terms = [];
  if (name) terms.push(name);
  for (const alias of entity?.aliases || []) {
    const clean = String(alias).trim();
    if (clean && clean.length >= 2) terms.push(clean);
  }

  for (const term of terms) {
    const regex = new RegExp(`(?<![A-Za-z0-9])${escapeRegExp(term)}(?![A-Za-z0-9])`, 'i');
    if (regex.test(source)) return true;
  }
  return false;
}

export function extractResolvedPlayers(steps, responseText) {
  const grouped = extractEntities(steps);
  const seenIds = new Set();
  const seenNames = new Set();
  const out = [];
  for (const entity of grouped.player || []) {
    const raw = entity?.raw;
    const id = raw?.player_id ?? raw?.id ?? raw?.player?.id ?? null;
    if (id == null) continue;
    if (!entityNameInText(entity, responseText)) continue;
    const idKey = String(id);
    const nameKey = String(entity?.name || '').trim().toLowerCase();
    if (seenIds.has(idKey)) continue;
    if (nameKey && seenNames.has(nameKey)) continue;
    seenIds.add(idKey);
    if (nameKey) seenNames.add(nameKey);
    out.push({ id: idKey, name: entity?.name || '' });
  }
  return out;
}

export function buildHighlightedEntitiesMarkdown(text, grouped, activeTypes = []) {
  if (!text || !isObject(grouped)) {
    return { markdown: text, entityById: {} };
  }

  const entityById = {};
  const terms = [];
  const active = new Set(activeTypes);

  for (const type of Object.keys(grouped)) {
    if (!active.has(type)) continue;
    const list = Array.isArray(grouped[type]) ? grouped[type] : [];

    for (const entity of list) {
      entityById[entity.id] = entity;

      const name = String(entity.name || '').replace(/\s+/g, ' ').trim();
      if (!name) continue;

      terms.push({ text: name, entityId: entity.id, priority: 3 });

      for (const alias of entity.aliases || []) {
        const clean = String(alias).trim();
        if (clean && clean.length >= 2) terms.push({ text: clean, entityId: entity.id, priority: 2 });
      }
    }
  }

  if (!terms.length) return { markdown: text, entityById };

  terms.sort((a, b) => b.priority - a.priority || b.text.length - a.text.length);

  const protectedRanges = getProtectedRanges(text);
  const used = [];
  const matches = [];
  const entityCount = {};
  const MAX_PER_ENTITY = 8;

  for (const term of terms) {
    const regex = new RegExp(`(?<![A-Za-z0-9])${escapeRegExp(term.text)}(?![A-Za-z0-9])`, 'gi');
    let match;
    while ((match = regex.exec(text)) !== null) {
      if ((entityCount[term.entityId] || 0) >= MAX_PER_ENTITY) break;
      const start = match.index;
      const end = start + match[0].length;
      if (isProtected(start, end, protectedRanges)) continue;
      if (used.some(([usedStart, usedEnd]) => start < usedEnd && end > usedStart)) continue;
      used.push([start, end]);
      matches.push({ start, end, entityId: term.entityId, text: match[0] });
      entityCount[term.entityId] = (entityCount[term.entityId] || 0) + 1;
    }
  }

  if (!matches.length) return { markdown: text, entityById };

  matches.sort((a, b) => a.start - b.start);

  let markdown = '';
  let cursor = 0;
  for (const match of matches) {
    markdown += text.slice(cursor, match.start);
    markdown += `<mark data-entity="${match.entityId}">${match.text}</mark>`;
    cursor = match.end;
  }
  markdown += text.slice(cursor);

  return { markdown, entityById };
}

function detectTool(toolName) {
  const name = String(toolName || '').toLowerCase();
  for (const tool of Object.keys(TOOL_META)) {
    if (name.includes(tool)) return tool;
  }
  return null;
}

function unwrapData(parsed) {
  if (isObject(parsed) && 'data' in parsed) return parsed.data;
  return parsed;
}

export function extractToolResults(steps) {
  const results = [];
  if (!Array.isArray(steps)) return results;

  for (const step of steps) {
    if (step?.tool_result == null) continue;
    const tool = detectTool(step?.tool_name);
    if (!tool) continue;
    const meta = TOOL_META[tool];
    results.push({ tool, label: meta.label, kind: meta.kind, data: unwrapData(toObject(step.tool_result)) });
  }

  return results;
}

function formatCell(value) {
  if (value == null) return '';
  if (isObject(value) || Array.isArray(value)) return namedString(value) || JSON.stringify(value);
  return String(value);
}

export function tableFromArray(arr) {
  if (!Array.isArray(arr) || !arr.length) return { columns: [], rows: [] };

  const objects = arr.filter(isObject);
  const columns = [];
  const seen = new Set();
  for (const item of objects) {
    for (const key of Object.keys(item)) {
      if (seen.has(key)) continue;
      seen.add(key);
      columns.push(key);
      if (columns.length >= 8) break;
    }
    if (columns.length >= 8) break;
  }

  const rows = arr.map(item => columns.map(col => (isObject(item) ? formatCell(item[col]) : formatCell(item))));

  return { columns: columns.map(humanize), rows };
}

export function flattenToRows(obj) {
  const rows = [];
  flattenObject(obj, '', rows);
  return rows;
}

function toNumber(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const num = Number(value);
    if (Number.isFinite(num)) return num;
  }
  return null;
}

function normalizeStatValue(value) {
  const direct = toNumber(value);
  if (direct != null) return direct;

  if (isObject(value)) {
    for (const key of ['total', 'average', 'sum', 'count', 'value']) {
      const num = toNumber(value[key]);
      if (num != null) return num;
    }
    for (const v of Object.values(value)) {
      const num = toNumber(v);
      if (num != null) return num;
    }
  }

  return null;
}

function entityRowMatch(row, entity) {
  const id = entity?.raw?.id;
  if (id != null && row?.entity_id != null && String(row.entity_id) === String(id)) return true;
  if (entity?.name && row?.entity_name === entity.name) return true;
  return false;
}

export function extractPlayerSeasonStats(results, entity) {
  const list = Array.isArray(results) ? results : [];
  const seasonResults = list.filter(result => result?.tool === 'get_season_stats');
  if (!seasonResults.length) return null;

  const rows = seasonResults.flatMap(result => (Array.isArray(result.data) ? result.data : []));
  const row =
    rows.find(item => isObject(item) && isObject(item.stats) && entityRowMatch(item, entity)) ||
    rows.find(item => isObject(item) && isObject(item.stats)) ||
    rows[0];

  if (!isObject(row) || !isObject(row.stats)) return null;

  const stats = {};
  Object.entries(row.stats).forEach(([key, value]) => {
    const num = normalizeStatValue(value);
    if (num != null) stats[key] = num;
  });

  const goals = row.stats.goals;
  let goalsBreakdown = null;
  if (isObject(goals)) {
    const total = toNumber(goals.total);
    if (total != null) {
      goalsBreakdown = { total, penalties: toNumber(goals.penalties) ?? 0 };
    }
  }

  return {
    seasonName: row.season_name || '',
    teamName: isObject(row.team) ? row.team.name || '' : '',
    stats,
    goalsBreakdown,
  };
}

const PLAYER_SPECIFIC_TOOLS = ['get_transfers', 'get_fixture_lineup_stats', 'get_topscorers', 'get_totw'];

function itemMatchesEntity(item, entity) {
  if (!isObject(item)) return false;

  const id = entity?.raw?.id;
  if (id != null) {
    for (const key of ['id', 'player_id', 'team_id', 'league_id', 'entity_id']) {
      if (item[key] != null && String(item[key]) === String(id)) return true;
    }
  }

  const name = String(entity?.name || '').trim().toLowerCase();
  if (!name) return false;

  for (const value of Object.values(item)) {
    if (typeof value === 'string' && value.trim().toLowerCase() === name) return true;
    if (isObject(value) && typeof value.name === 'string' && value.name.trim().toLowerCase() === name) return true;
  }
  return false;
}

export function filterEntitySpecificResults(results, entity, tools) {
  const list = Array.isArray(results) ? results : [];
  const out = [];

  for (const result of list) {
    if (!tools.includes(result?.tool)) continue;
    const data = result.data;
    if (Array.isArray(data)) {
      const filtered = data.filter(item => itemMatchesEntity(item, entity));
      if (filtered.length) out.push({ ...result, data: filtered });
    } else if (itemMatchesEntity(data, entity)) {
      out.push(result);
    }
  }

  return out;
}

export function filterPlayerSpecificResults(results, entity) {
  return filterEntitySpecificResults(results, entity, PLAYER_SPECIFIC_TOOLS);
}
